// Trimmed copy of GA_Elements/gaReporting.js's period-scoping + formatting
// helpers — same duplicate-per-section convention the other 9 dashboards
// use, kept here rather than imported cross-folder so Global Dashboard
// doesn't depend on GA_Elements surviving unchanged. Only the pieces
// global_summary's response shape actually needs are included (no
// record-level filterRecords/recordMetrics — Global has no record-level
// drill-down).

// Department labels from the backend (global_views/dashboard.py's
// DEPARTMENTS config) are the exact same strings every department's own
// sidebar/tab uses — "G&A Dashboard", "Sales APAC Dashboard", etc. — since
// that full label is also the value every filter/fetch/lookup in this
// folder matches against (the page-level Department filter, /api/global/
// raw/'s department param, row.department equality checks). Stripping the
// trailing " Dashboard" only at render time (never touching the value
// itself) avoids repeating that word in every row/card/title on this page
// without risking a mismatch anywhere it's compared or sent to the backend.
export function shortDeptLabel(label) {
    return (label || '').replace(/ Dashboard$/, '');
}

export function monthInScope(month, { quarter = [], customRange = null, period = [], asOf = null, yearMode = 'current', priorYear = '' } = {}) {
    const displayMonth = yearMode === 'prior' ? `${priorYear}-${month.slice(5)}` : month;
    if (customRange && (displayMonth < customRange.start || displayMonth > customRange.end)) return false;
    // Quarter/Period are multi-select (array of selected values) — a month
    // is in scope if it matches ANY selected quarter, and an empty selection
    // (same as the old '' / default 'fy') means no restriction at all.
    const quarters = Array.isArray(quarter) ? quarter : (quarter ? [quarter] : []);
    if (quarters.length && !quarters.some(q => Math.ceil(Number(month.slice(5, 7)) / 3) === Number(q.slice(1)))) return false;
    const periods = Array.isArray(period) ? period : (period ? [period] : []);
    // Full Year (or no selection) includes every scoped month; Year to Date
    // alone restricts to months up to asOf. Selecting both is a no-op toward
    // Full Year, since YTD's months are always a subset of FY's.
    if (periods.includes('ytd') && !periods.includes('fy')) return !!asOf && month <= asOf;
    return true;
}

// Fiscal Year is multi-select too, but blending "Current" + "Prior" means
// summing two structurally different data lenses (prior year substitutes
// its own actual into the "actual" slot, with no AOP) rather than just
// loosening an equality check — so scopeSummaryRow/scopedMetricValue below
// run the normal single-year-mode logic once per selected mode and sum the
// results, instead of threading array-awareness through monthInScope.
function normalizeYearModes(yearMode) {
    const modes = Array.isArray(yearMode) ? yearMode : (yearMode ? [yearMode] : []);
    return modes.length ? modes : ['current'];
}

export function sumMetrics(rows) {
    return rows.reduce((sum, row) => ({ actual: sum.actual + row.actual, aop: sum.aop + row.aop, py: sum.py + row.py }), { actual: 0, aop: 0, py: 0 });
}

// Popup record-detail filters (GlobalRecordDetails) — identical to every
// department dashboard's own gaReporting.js copy.
export function filterRecords(records, source, filters = {}) {
    const fields = source === 'hc'
        ? [['hc_type', filters.hcType], ['status', filters.status], ['country', filters.country]]
        : [['sub_category', filters.subCategory]];
    return (records || []).filter(r => fields.every(([key, value]) => !value || r[key] === value));
}

// A single raw record's own $ total for the given months — every
// department's own fiscal module defines the identical CURRENT_YEAR/
// PRIOR_YEAR/block-naming convention (actual_fcst_<year>/aop_<year>/
// actual_<year>), verified session-wide, so this one copy works no matter
// which department the record came from.
export function recordMetrics(record, months, currentYear, priorYear) {
    const values = record.monthly_values || {};
    const sum = (block, year) => months.reduce((n, month) => n + Number(values[block]?.[`${year}-${month.slice(5)}`] || 0), 0);
    if (!priorYear) return { actual: sum(`actual_${currentYear}`, currentYear), aop: 0, py: 0 };
    return {
        actual: sum(`actual_fcst_${currentYear}`, currentYear),
        aop: sum(`aop_${currentYear}`, currentYear),
        py: sum(`actual_${priorYear}`, priorYear),
    };
}

export function scopeSummaryRow(row, options) {
    // Build one filtered+remapped monthly series per selected Fiscal Year
    // mode, then merge them by month (summing actual/aop/py) — so Current +
    // Prior selected together show one blended series instead of just
    // whichever mode happened to run first. The common case (one mode
    // selected) merges a single series with itself, a no-op.
    const perMode = normalizeYearModes(options.yearMode).map(ym => (row?.monthly || [])
        .filter(m => monthInScope(m.month, { ...options, yearMode: ym }))
        .map(m => ym === 'prior' ? { ...m, actual: m.py, aop: 0, py: 0 } : m));
    const byMonth = new Map();
    perMode.flat().forEach(m => {
        const existing = byMonth.get(m.month);
        byMonth.set(m.month, existing
            ? { ...existing, actual: existing.actual + m.actual, aop: existing.aop + m.aop, py: existing.py + m.py }
            : { ...m });
    });
    const monthly = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
    const totals = sumMetrics(monthly);
    const ytd = sumMetrics(monthly.filter(m => options.asOf && m.month <= options.asOf));
    const quarterly = Object.fromEntries([1, 2, 3, 4].map(q => [`Q${q}`, sumMetrics(monthly.filter(m => Math.ceil(Number(m.month.slice(5, 7)) / 3) === q))]));
    return {
        ...row, monthly, quarterly,
        fy_actual_forecast: totals.actual, fy_aop: totals.aop, fy_prior_year: totals.py,
        ytd_actual: ytd.actual, ytd_aop: ytd.aop, ytd_prior_year: ytd.py,
        act_vs_aop_ytd: ytd.actual - ytd.aop, act_vs_py_ytd: ytd.actual - ytd.py,
        fcst_vs_aop_full_year: totals.actual - totals.aop, fcst_vs_py_full_year: totals.actual - totals.py,
    };
}

// Every figure here follows the same "$XXXK, always thousands" convention
// as every other dashboard in the app.
export function money(value) {
    const num = Number(value || 0);
    const sign = num < 0 ? '-' : '';
    const thousands = Math.round(Math.abs(num) / 1000);
    return `${sign}$${thousands.toLocaleString('en-US')}K`;
}

export function variancePct(actual, base) {
    return base ? `${((actual - base) / Math.abs(base) * 100).toFixed(1)}%` : 'N/A (zero base)';
}

// Same balance-vs-flow collapsing rule as GA_Elements/gaReporting.js's
// scopedMetricValue — Ending ARR is a point-in-time balance (last month in
// scope), Revenue is a flow (summed across scoped months).
export function scopedMetricValue(metric, field, options) {
    if (!metric) return null;
    // Same per-mode-then-sum approach as scopeSummaryRow, for the same
    // reason — see its comment above.
    return normalizeYearModes(options.yearMode).reduce((sum, ym) => {
        const inScope = (metric.monthly || []).filter(m => monthInScope(m.month, { ...options, yearMode: ym }));
        if (!inScope.length) return sum;
        const pick = row => (ym === 'prior' ? row.py : row[field]) || 0;
        return sum + (metric.is_balance ? pick(inScope[inScope.length - 1]) : inScope.reduce((s, row) => s + pick(row), 0));
    }, 0);
}

// Global spend as a % of a Revenue/ARR denominator — null (not 0) when the
// denominator is missing/zero, so callers can show "Unavailable" instead of
// a misleading 0% or Infinity.
export function pctOfMetric(totalValue, metricValue) {
    if (totalValue == null || metricValue == null || !metricValue) return null;
    return (totalValue / metricValue) * 100;
}

// Re-derives a "Total" row (same shape scopeSummaryRow produces — monthly
// series + fy_*/run_rate fields) from a filtered subset of category rows,
// so every table's existing Total footer, chart data, and % calculations
// work unchanged whether the page-level Department/Category filters are
// narrowing the view or not.
export function combineRows(rows) {
    const monthsMap = {};
    rows.forEach(r => (r.monthly || []).forEach(m => {
        if (!monthsMap[m.month]) monthsMap[m.month] = { month: m.month, label: m.label, actual: 0, aop: 0, py: 0 };
        monthsMap[m.month].actual += m.actual || 0;
        monthsMap[m.month].aop += m.aop || 0;
        monthsMap[m.month].py += m.py || 0;
    }));
    const monthly = Object.values(monthsMap).sort((a, b) => a.month.localeCompare(b.month));
    const sum = key => rows.reduce((s, r) => s + (r[key] || 0), 0);
    const run_rate = Object.fromEntries(['current', 'dec_aop', 'dec_fcst'].map(k => [k, rows.reduce((s, r) => s + (r.run_rate?.[k] || 0), 0)]));
    run_rate.issues = [];
    const fyActual = sum('fy_actual_forecast');
    const fyAop = sum('fy_aop');
    const fyPy = sum('fy_prior_year');
    return {
        label: 'Total', monthly, department: null,
        fy_actual_forecast: fyActual, fy_aop: fyAop, fy_prior_year: fyPy,
        fcst_vs_aop_full_year: fyActual - fyAop, fcst_vs_py_full_year: fyActual - fyPy,
        run_rate,
    };
}

// Month-by-month % of Revenue/ARR for GlobalPctDeepDiveModal's Monthly
// Detail table — divides each month's $ total by that SAME month's
// Revenue/ARR value (not the scoped/FY total used for the headline % cards),
// so a slow-revenue month still shows its own real ratio instead of
// borrowing the whole period's blended denominator.
export function monthlyPctRows(monthlyTable, metricSeries, months) {
    const byMonth = Object.fromEntries((metricSeries?.monthly || []).map(m => [m.month, m]));
    const monthSet = new Set(months);
    return (monthlyTable || []).filter(row => monthSet.has(row.month)).map(row => {
        const denom = byMonth[row.month] || {};
        const pct = (num, field) => (denom[field] ? (num / denom[field]) * 100 : null);
        const actualPct = pct(row.actual, 'actual');
        const aopPct = pct(row.aop, 'aop');
        return {
            label: row.label,
            actual: actualPct,
            aop: aopPct,
            py: pct(row.py, 'py'),
            // Point-difference between this month's own Actual% and AOP%
            // (not a $ variance ÷ revenue) — same reasoning as the headline
            // vs. AOP Variance card, so this breakdown always reconciles.
            variance: actualPct != null && aopPct != null ? actualPct - aopPct : null,
            // Raw numerator/denominator behind each % above — same shape for
            // every column — so GlobalPctDeepDiveModal's formula popup can
            // show the actual numbers plugged in, not just the % result.
            // Variance has no single num/denom pair (it's a difference of
            // two percentages), so its formula popup falls back to text-only.
            _raw: {
                actual: { num: row.actual, denom: denom.actual },
                aop: { num: row.aop, denom: denom.aop },
                py: { num: row.py, denom: denom.py },
            },
        };
    });
}

// Each "{Department} — {Category}" row's own $ total as a % of the SAME
// scoped Revenue/ARR denominator the headline % cards use (Revenue/ARR
// isn't tracked per department/category, so every row's % is its own share
// of the one company figure) — rows should already be scoped (scopeSummaryRow)
// by the caller.
export function categoryPctRows(rows, denomActual, denomAop, denomPy) {
    return rows.map(row => {
        const actualPct = denomActual ? (row.fy_actual_forecast / denomActual) * 100 : null;
        const aopPct = denomAop ? (row.fy_aop / denomAop) * 100 : null;
        return {
            label: row.department ? `${shortDeptLabel(row.department)} — ${row.category}` : row.label,
            // Kept alongside the combined label above so a caller spanning more
            // than one department (GlobalPctDeepDiveModal's company-wide "By
            // Department & Category" table) can group rows back into one row
            // per department before drilling into any one of them.
            department: row.department || null,
            category: row.category || null,
            actual: actualPct,
            aop: aopPct,
            py: denomPy ? (row.fy_prior_year / denomPy) * 100 : null,
            // Point-difference between this row's own Actual% and AOP% —
            // see monthlyPctRows' identical comment.
            variance: actualPct != null && aopPct != null ? actualPct - aopPct : null,
            _raw: {
                actual: { num: row.fy_actual_forecast, denom: denomActual },
                aop: { num: row.fy_aop, denom: denomAop },
                py: { num: row.fy_prior_year, denom: denomPy },
            },
        };
    });
}

// Same company-wide-denominator convention, but for each row's own
// annualized Run Rate (current/dec_aop/dec_fcst) instead of a $ total.
// Mapped onto the same generic {actual, aop, py, variance} column slots
// GlobalPctDeepDiveModal's Category Breakdown table uses for both groups
// (Current -> actual, Dec AOP -> aop, Dec Fcst -> py) — only the column
// headers passed alongside it change.
export function categoryRunRatePctRows(rows, denomCurrent, denomDecAop, denomDecFcst) {
    return rows
        .filter(r => r.run_rate && (r.run_rate.current || r.run_rate.dec_aop || r.run_rate.dec_fcst))
        .map(r => {
            const rr = r.run_rate;
            const actual = denomCurrent && rr.current != null ? (rr.current / denomCurrent) * 100 : null;
            const aop = denomDecAop && rr.dec_aop != null ? (rr.dec_aop / denomDecAop) * 100 : null;
            const py = denomDecFcst && rr.dec_fcst != null ? (rr.dec_fcst / denomDecFcst) * 100 : null;
            const variance = denomCurrent && rr.dec_fcst != null && rr.dec_aop != null ? ((rr.dec_fcst - rr.dec_aop) / denomCurrent) * 100 : null;
            return {
                label: r.department ? `${shortDeptLabel(r.department)} — ${r.category}` : r.label, actual, aop, py, variance,
                department: r.department || null,
                category: r.category || null,
                _raw: {
                    actual: { num: rr.current, denom: denomCurrent },
                    aop: { num: rr.dec_aop, denom: denomDecAop },
                    py: { num: rr.dec_fcst, denom: denomDecFcst },
                    variance: { num: rr.dec_fcst != null && rr.dec_aop != null ? rr.dec_fcst - rr.dec_aop : null, denom: denomCurrent },
                },
            };
        });
}

// One consolidated {actual, aop, py} row per department, per source (HC/
// Non-HC/Commission) — built from each category row's own `.hc`/`.nonhc`/
// `.commission` sub-row FY totals (global_views/summary.py attaches these),
// summed across every one of that department's categories. Same
// consolidation convention as the Summary tab's departmentSummaryRows, just
// split by source instead of combined, for the HC & Non-HC 360 tab's own
// department-level rows. Commission only exists for the Sales APAC/NAM/
// Solution departments — a department only gets a commission[] entry (and
// therefore a row) when at least one of its category rows actually carries
// a `.commission` sub-row, same "don't show a phantom $0 row for
// departments with no such data" convention the per-department 360 tabs use
// (`data.commission_costs && ...`).
export function departmentHcNonhcRows(byCategory, departmentFilter) {
    const departments = Array.isArray(departmentFilter) ? departmentFilter : (departmentFilter ? [departmentFilter] : []);
    const hc = {};
    const nonhc = {};
    const commission = {};
    Object.values(byCategory).forEach(row => {
        if (departments.length && !departments.includes(row.department)) return;
        if (!hc[row.department]) hc[row.department] = { actual: 0, aop: 0, py: 0 };
        if (!nonhc[row.department]) nonhc[row.department] = { actual: 0, aop: 0, py: 0 };
        hc[row.department].actual += row.hc?.fy_actual_forecast || 0;
        hc[row.department].aop += row.hc?.fy_aop || 0;
        hc[row.department].py += row.hc?.fy_prior_year || 0;
        nonhc[row.department].actual += row.nonhc?.fy_actual_forecast || 0;
        nonhc[row.department].aop += row.nonhc?.fy_aop || 0;
        nonhc[row.department].py += row.nonhc?.fy_prior_year || 0;
        if (row.commission) {
            if (!commission[row.department]) commission[row.department] = { actual: 0, aop: 0, py: 0 };
            commission[row.department].actual += row.commission.fy_actual_forecast || 0;
            commission[row.department].aop += row.commission.fy_aop || 0;
            commission[row.department].py += row.commission.fy_prior_year || 0;
        }
    });
    // No explicit sort — hc/nonhc/commission's own insertion order already
    // follows byCategory's order, which follows DEPARTMENTS in
    // global_views/dashboard.py, the one fixed sequence every department
    // list across the app now shows.
    const toRows = map => Object.keys(map).map(department => ({ label: shortDeptLabel(department), department, ...map[department] }));
    return { hcRows: toRows(hc), nonhcRows: toRows(nonhc), commissionRows: toRows(commission) };
}

// Applies the page-level Category filter (Total / Headcount / Non-Headcount /
// Commission — same "Category" filter every individual department
// dashboard's own Dashboard/Summary/HC & Non-HC 360 tabs already have) to a
// `global_summary` response, by swapping every row (the combined Total, and
// each "{Department} — {Category}" row) for the SUM of whichever `.hc`/
// `.nonhc`/`.commission` sub-rows are selected — all three already computed
// server-side with the same shape as the row itself (monthly series + fy_*/
// run_rate fields), so every consumer (GlobalDashboard.js, GlobalSummary.js,
// the deep-dive/run-rate drill modals) keeps working completely unchanged;
// they just read a narrower (or combined) set of numbers. An empty selection
// means Total (same meaning as the old single-select 'total'/''); selecting
// more than one source sums them together via combineRows, the same generic
// multi-row combiner the page's own Total/department rollups already use.
// `department`/`category` aren't part of the sub-row shape (only the combined
// row carries them), so they're re-attached here from the original row.
export function applyCostCategory(data, costCategory) {
    const selected = Array.isArray(costCategory) ? costCategory : (costCategory ? [costCategory] : []);
    if (!selected.length) return data;
    // The `.hc`/`.nonhc`/`.commission` sub-rows stay attached (untouched, real
    // figures) even once a subset has become the headline — otherwise the
    // Headcount vs. Non-Headcount split drill-down (which reads them off
    // whatever row it's given) would lose the very data it exists to show.
    const pick = row => {
        if (!row) return row;
        const subRows = selected.map(k => row[k]).filter(Boolean);
        if (!subRows.length) return row;
        const combined = subRows.length === 1 ? subRows[0] : combineRows(subRows);
        return { ...combined, department: row.department, category: row.category, hc: row.hc, nonhc: row.nonhc, commission: row.commission };
    };
    const sumDeptFields = d => {
        const run_rate = Object.fromEntries(['current', 'dec_aop', 'dec_fcst'].map(rk => [
            rk, selected.reduce((s, k) => s + (d[`${k}_run_rate`]?.[rk] || 0), 0),
        ]));
        run_rate.issues = [];
        return {
            total_fy_actual: selected.reduce((s, k) => s + (d[`${k}_fy_actual`] || 0), 0),
            total_fy_aop: selected.reduce((s, k) => s + (d[`${k}_fy_aop`] || 0), 0),
            run_rate,
        };
    };
    return {
        ...data,
        global_total: {
            ...data.global_total,
            total: pick(data.global_total.total),
            by_category: Object.fromEntries(Object.entries(data.global_total.by_category).map(([key, row]) => [key, pick(row)])),
        },
        department_breakdown: (data.department_breakdown || []).map(d => ({ ...d, ...sumDeptFields(d) })),
    };
}

// Department and Category dropdown option lists for the page-level filter
// bar — Category is scoped to whichever Department(s) are selected (or
// every department's categories when none is), since category names don't
// mean anything shared across departments. Department is multi-select, so
// the category list is the UNION of every selected department's own
// categories.
export function departmentCategoryOptions(data, departmentFilter) {
    const rows = Object.values(data?.global_total?.by_category || {});
    // No explicit sort — rows' own order already follows DEPARTMENTS in
    // global_views/dashboard.py, the one fixed sequence every department
    // list across the app now shows.
    const departmentOptions = Array.from(new Set(rows.map(r => r.department)));
    const departments = Array.isArray(departmentFilter) ? departmentFilter : (departmentFilter ? [departmentFilter] : []);
    const categoryOptions = Array.from(new Set(
        rows.filter(r => !departments.length || departments.includes(r.department)).map(r => r.category),
    )).sort();
    return { departmentOptions, categoryOptions };
}
